import ResponsiveContent from "@/elements/components/shared/responsive-content";
import { AppBar } from "@/elements/components/ui/app-bar";
import React, { useState } from "react";
import { ScrollView, View, Text } from "react-native";
import { useRouter, useLocalSearchParams, Redirect } from "expo-router";
import StudyMaterialIllustration from "@/assets/illustrations/study-material-illustration";
import StudyMaterialUploader from "@/elements/components/lesson-history/study-material-uploader";
import { Id } from "@teachloop/backend/_generated/dataModel";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "@/elements/components/utils/convex-api";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import FileIcon from "@/assets/svgs/file-icon";
import { Button } from "@/elements/components/ui/button";
import Loader from "@/elements/components/shared/loader";
import ReportSubmittedModal from "@/elements/components/modals/report-submitted-modal";
import goBack from "@/elements/components/utils/go-back";

function StudyMaterialsScreen() {
  const router = useRouter();
  const { lessonReportId } = useLocalSearchParams<{
    lessonReportId?: Id<"lessonReports">;
  }>();
  const { data: lessonReport, isLoading } = useQuery(
    convexQuery(
      api.tutor.lessonHistory.queries.getLessonReport,
      lessonReportId
        ? {
            lessonReportId: lessonReportId,
          }
        : "skip"
    )
  );
  const files = lessonReport?.files || [];
  const { mutateAsync: submitLessonReport, isPending } = useMutation({
    mutationFn: useConvexMutation(
      api.tutor.lessonReports.mutations.submitLessonReport
    ),
  });
  const [isReportSubmittedModalVisible, setIsReportSubmittedModalVisible] =
    useState(false);

  if (isLoading) {
    return <Loader />;
  }

  if (!lessonReportId) {
    return <Redirect href="/" />;
  }

  return (
    <View className="flex-1 bg-teachLoop-creamBase">
      <AppBar>
        <AppBar.Icon onPress={goBack} />
        <AppBar.Title>Back</AppBar.Title>
      </AppBar>
      <ScrollView showsVerticalScrollIndicator={false}>
        <ResponsiveContent>
          <View className="mx-5">
            <View className="h-2" />
            <View className="gap-1">
              <Text className="font-zilla-slab-medium leading-[1.2] font-medium text-[28px] text-black">
                Add study material
              </Text>
              <Text className="font-geologica-regular font-normal text-xl text-black leading-[1.5]">
                Please attach relevant worksheets, links and documents to the
                report. You can also do this later from
              </Text>
            </View>

            <View className="h-8" />

            {files.length === 0 ? (
              <View className="gap-4 bg-teachLoop-creamDark items-center rounded-[17] px-6 py-9">
                <StudyMaterialIllustration />

                <StudyMaterialUploader
                  lessonReportId={lessonReportId as Id<"lessonReports">}
                />

                <Text className="font-geologica-regular font-normal text-sm text-black leading-[1.5]">
                  You can only attach images, PDFs, Word and Xcel files
                </Text>
              </View>
            ) : (
              <View className="gap-4">
                <Text className="font-geologica-medium font-medium text-base text-black leading-[1.5]">
                  Files attached
                </Text>
                <View className="gap-2">
                  {files.map((file) => (
                    <View
                      key={file.storageId}
                      className="flex-row gap-2 items-center"
                    >
                      <FileIcon
                        className="size-4"
                        pathClassName="stroke-teachLoop-accentPrimary"
                      />
                      <Text className="flex-1 text-base font-geologica-medium text-teal-600">
                        {file.fileName}
                      </Text>
                    </View>
                  ))}
                </View>
                <StudyMaterialUploader
                  lessonReportId={lessonReportId as Id<"lessonReports">}
                />
              </View>
            )}

            {files.length === 0 ? (
              <View className="h-[90px]" />
            ) : (
              <View className="h-9" />
            )}

            <View className="mx-10">
              <Button
                size="md"
                fullWidth
                onPress={async() => {
                  await submitLessonReport({
                    lessonReportId,
                  });
                  setIsReportSubmittedModalVisible(true);
                }}
                disabled={isPending}
              >
                <Button.Text size="md">Submit report</Button.Text>
              </Button>
            </View>
          </View>
        </ResponsiveContent>
      </ScrollView>

      <ReportSubmittedModal
        isVisible={isReportSubmittedModalVisible}
        onClose={() => {
          setIsReportSubmittedModalVisible(false);
          router.replace("/");
        }}
      />
    </View>
  );
}

export default StudyMaterialsScreen;
